import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import { CheckCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
        <div className="flex-1 flex items-center justify-center px-5 py-10">
          <div className="text-center space-y-4 animate-entrance">
            <h2 className="text-2xl font-bold text-[#1D1D1F]">Invalid reset link</h2>
            <p className="text-sm text-[#86868B]">This password reset link is invalid or has expired.</p>
            <Link to="/forgot-password" className="text-[#007AFF] font-medium hover:underline text-sm">
              Request a new reset link
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-5 py-10 sm:p-8">
        <div className="w-full max-w-md space-y-6 animate-entrance">
          <div className="flex items-center justify-center mb-4">
            <span className="text-2xl brand-name">Zelvrix</span>
          </div>

          {!success ? (
            <>
              <div className="text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1D1D1F]">Set new password</h2>
                <p className="mt-2 text-sm text-[#86868B]">Your new password must be at least 8 characters with 1 uppercase letter and 1 number.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm rounded-xl" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1D1D1F] font-medium text-sm">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 chars, 1 uppercase, 1 number"
                      required
                      autoComplete="new-password"
                      className="input-base h-12 rounded-xl pr-12 text-base"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#1D1D1F] font-medium text-sm">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    className={`input-base h-12 rounded-xl text-base ${confirmPassword && password !== confirmPassword ? 'ring-2 ring-[#FF3B30]/50' : ''}`}
                    data-testid="reset-confirm-password-input"
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-[#FF3B30] mt-1">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary h-12 rounded-xl text-base"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4" data-testid="reset-success">
              <div className="w-16 h-16 bg-[#34C759]/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={32} weight="fill" className="text-[#34C759]" />
              </div>
              <h2 className="text-2xl font-bold text-[#1D1D1F]">Password reset!</h2>
              <p className="text-sm text-[#86868B]">Your password has been updated. You can now sign in.</p>
              <Link
                to="/login"
                className="inline-block btn-primary px-6 py-3 rounded-xl text-base"
                data-testid="go-to-login-btn"
              >
                Go to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
